import {
  HONEST_JOBS_JOB_DETAIL_FIELD_KEYS,
  HONEST_JOBS_RESOURCE_DETAIL_FIELD_KEYS,
} from '../constants/reentry-planning.constants';
import { mapReentryPlanJobToHonestJobsResource } from './map-reentry-plan-job.util';
import { mapReentryPlanResourceToHonestJobsResource } from './map-reentry-plan-resource.util';

export const HONEST_JOBS_WIDGET_TYPE = 'honest-jobs';
const HONEST_JOBS_RESOURCE_TYPE = 'Honest Jobs';

const HONEST_JOBS_JOBS_WIDGET_TYPE = 'honest_jobs_jobs';
const HONEST_JOBS_RESOURCES_WIDGET_TYPE = 'honest_jobs_resources';

const REENTRY_PLAN_WIDGET_TYPES = new Set([
  HONEST_JOBS_JOBS_WIDGET_TYPE,
  HONEST_JOBS_RESOURCES_WIDGET_TYPE,
]);

const HONEST_JOBS_API_WIDGET_TYPES = new Set([
  HONEST_JOBS_WIDGET_TYPE,
  'honest_jobs',
  HONEST_JOBS_JOBS_WIDGET_TYPE,
  HONEST_JOBS_RESOURCES_WIDGET_TYPE,
]);

const EMPTY_REENTRY_PLAN_ITEMS = Object.freeze({
  jobs: [],
  resourcesByCategory: {},
});

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

export const isHonestJobsApiWidgetType = (widgetType) =>
  HONEST_JOBS_API_WIDGET_TYPES.has(widgetType);

export const widgetTypeRequiresReentryPlan = (widgetType) =>
  REENTRY_PLAN_WIDGET_TYPES.has(widgetType);

const normalizeCategoryKey = (value) => String(value ?? '').trim().toLowerCase();

const getGroupJobs = (group) => (Array.isArray(group?.jobs) ? group.jobs : []);

const getGroupResources = (group) =>
  Array.isArray(group?.resources) ? group.resources : [];

const appendItemsToCategoryMap = (categoryMap, categoryName, items) => {
  const categoryKey = normalizeCategoryKey(categoryName);
  if (!categoryKey || items.length === 0) {
    return categoryMap;
  }

  const existingItems = categoryMap[categoryKey] ?? [];
  return { ...categoryMap, [categoryKey]: [...existingItems, ...items] };
};

/** Collects reentry-plan jobs and category-keyed resources from group arrays. */
export const buildReentryPlanItems = (reentryPlan) => {
  if (!Array.isArray(reentryPlan?.groups)) {
    return EMPTY_REENTRY_PLAN_ITEMS;
  }

  return reentryPlan.groups.reduce(
    (items, group) => ({
      jobs: [
        ...items.jobs,
        ...getGroupJobs(group).map(mapReentryPlanJobToHonestJobsResource),
      ],
      resourcesByCategory: appendItemsToCategoryMap(
        items.resourcesByCategory,
        group?.name,
        getGroupResources(group).map(mapReentryPlanResourceToHonestJobsResource),
      ),
    }),
    EMPTY_REENTRY_PLAN_ITEMS,
  );
};

const resolveHonestJobsTitle = (config) => config.title ?? config.category ?? '';

const resolveHonestJobsResources = (config) =>
  Array.isArray(config.resources) ? config.resources : [];

const isHonestJobsJobsWidgetType = (widgetType) => widgetType === HONEST_JOBS_JOBS_WIDGET_TYPE;

const isHonestJobsResourcesWidgetType = (widgetType) =>
  widgetType === HONEST_JOBS_RESOURCES_WIDGET_TYPE;

const resolveResourcesForCategory = (resourcesByCategory, categoryValue) => {
  const categoryKey = normalizeCategoryKey(categoryValue);
  if (!categoryKey) {
    return [];
  }

  const resources = resourcesByCategory[categoryKey];
  return Array.isArray(resources) ? resources : [];
};

const resolveHonestJobsResourcesFromReentryPlan = (
  widgetType,
  config,
  reentryPlanItems = EMPTY_REENTRY_PLAN_ITEMS,
) => {
  const jobs = reentryPlanItems.jobs ?? [];
  const resourcesByCategory = reentryPlanItems.resourcesByCategory ?? {};

  let reentryResources = [];
  if (isHonestJobsJobsWidgetType(widgetType)) {
    reentryResources = jobs;
  } else if (isHonestJobsResourcesWidgetType(widgetType)) {
    reentryResources = resolveResourcesForCategory(
      resourcesByCategory,
      config.category ?? config.title,
    );
  }

  if (reentryResources.length > 0) {
    return reentryResources;
  }

  return resolveHonestJobsResources(config);
};

const pickTruthyConfigField = (config, key) => (config[key] ? { [key]: config[key] } : {});

const pickNullableNumberConfigField = (config, key) =>
  config[key] != null ? { [key]: config[key] } : {};

const pickHonestJobsOptionalConfig = (config) => ({
  ...pickNullableNumberConfigField(config, 'initialVisibleCount'),
  ...pickTruthyConfigField(config, 'moreInfoLabel'),
  ...pickTruthyConfigField(config, 'emptyStateTitle'),
  ...pickTruthyConfigField(config, 'emptyStateDescription'),
});

const resolveHonestJobsResourceOrJobDetailFieldKeys = (widgetType, config) => {
  if (Array.isArray(config.detailFieldKeys)) {
    return config.detailFieldKeys;
  }

  return isHonestJobsJobsWidgetType(widgetType)
    ? HONEST_JOBS_JOB_DETAIL_FIELD_KEYS
    : HONEST_JOBS_RESOURCE_DETAIL_FIELD_KEYS;
};

export const mapHonestJobsWidgetProps = (
  widget,
  reentryPlanItems = EMPTY_REENTRY_PLAN_ITEMS,
) => {
  const config = isPlainObject(widget?.config) ? widget.config : {};
  const widgetType = widget?.widgetType ?? '';

  return {
    title: resolveHonestJobsTitle(config),
    resources: resolveHonestJobsResourcesFromReentryPlan(
      widgetType,
      config,
      reentryPlanItems,
    ),
    resourceType: config.resourceType ?? HONEST_JOBS_RESOURCE_TYPE,
    detailFieldKeys: resolveHonestJobsResourceOrJobDetailFieldKeys(widgetType, config),
    ...pickHonestJobsOptionalConfig(config),
  };
};
